import { CreateBrandFileData } from '@anm/api/modules/brands';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { brandActions, brandSelectors } from './../../../../store/modules/brands';
import { hostedVideoSelectors } from './../../../../store/modules/hostedVideo';
import { userSelectors } from './../../../../store/modules/user';
import { videoFolderSelectors } from './../../../../store/modules/videoFolder';
import { BrandFileCategories, BrandFileTypes } from './useBrandTypes';

type HookProps = {
  onSucess: () => void;
  fileType: BrandFileCategories;
  projectId: string;
  fileName?: string;
};

const useAddToBrand = ({ fileType, projectId, fileName, onSucess }: HookProps) => {
  const dispatch = useDispatch();

  const userId = useSelector(userSelectors.selectUserId);
  const project = useSelector(state => videoFolderSelectors.selectProjectById(state, projectId));
  const videoUrl = useSelector(hostedVideoSelectors.selectVideoUrl);
  const currentBrandId = useSelector(brandSelectors.selectCurrentBrandId);
  const defaultPreview = project?.preview_metadata[0];
  const isPending = useSelector(brandSelectors.selectIsCreateBrandFilePending);
  const createdFile = useSelector(brandSelectors.selectIsCreateBrandFilePending);
  const smallestPreview =
    project && useSelector(state => videoFolderSelectors.selectSmallestProjectPreview(state, projectId));

  const { width, height, duration } = project || {};

  useEffect(() => {
    if (!createdFile) return;

    onSucess();
  }, [createdFile]);

  const addToBrand = useCallback(() => {
    if (!userId || !currentBrandId) return;

    const newBrandFile: CreateBrandFileData = {
      userId,
      preview: smallestPreview || defaultPreview || '',
      type: BrandFileTypes.project,
      brandId: currentBrandId,
      category: fileType,
      sourceId: projectId,
      meta: {
        name: fileName || project?.name || 'Untitled video',
        width,
        height,
        duration,
        hidden: false,
        ...(videoUrl && { src: videoUrl })
      }
    };

    dispatch(
      brandActions.createBrandFile({
        data: newBrandFile,
        brandId: currentBrandId
      })
    );
  }, [userId, project, fileName, videoUrl, currentBrandId, defaultPreview, fileType, projectId]);

  return [isPending, addToBrand] as const;
};

export default useAddToBrand;
